import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';

import { FaPlus } from 'react-icons/fa';
import { IoIosWarning } from 'react-icons/io';
import { BiImage, BiTrash, BiX } from 'react-icons/bi';

import { AxiosError } from 'axios';

import Switch from '../../../../../../newComponents/Switch';
import Button from '../../../../../../newComponents/Button';
import Input from '../../../../../../newComponents/UnformInput';
import Select from '../../../../../../newComponents/UnformSelect';
import FileInput from '../../../../../../newComponents/FileInput';
import { ModalHandles } from '../../../../../../newComponents/Modal';
import CustomCheckbox from '../../../../../../newComponents/CustomCheckbox';

import { useToast } from '../../../../../../hooks/toast';
import { Grid, useGridProduct } from '../../../../../../hooks/gridProduct';

import PlaceholderGridImage from '../../../../../../assets/no_image.png';

import {
  Container,
  Header,
  GridsList,
  GridItem,
  GridImage,
  GridItemHeader,
  GridItemBody,
  DeleteButton,
  ModalHeader,
  Modal,
  HelpButton,
  CloseModalButton,
  ModalBody,
  GenerateGridsTable,
  GenerateGridsTableHeader,
  GenerateGridsTableBody,
  ContainerButton,
} from './styles';
import { EditColorsButton } from '../../../GridGroup/steps/Colors/styles';
import { EditSizesButton } from '../../../GridGroup/steps/Sizes/styles';
import ProductsService from '../../../../../../services/ProductsService';
import { useAuth } from '../../../../../../hooks/auth';
import { CustomInput } from '../../../../styles';
import { newDebounce } from '../../../../../../utils/debounce';

export const GridsFormSchema = Yup.object().shape({
  grids: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .max(40, 'No máximo 40 caracteres')
        .required('O nome da grade é obrigatório'),
      size: Yup.string().required('O tamanho é obrigatório'),
      color: Yup.string().required('A cor é obrigatória'),
      code: Yup.string().max(40, 'No máximo 20 caracteres'),
      price: Yup.number()
        .min(0.01, 'Preço inválido')
        .required('O preço é obrigatório')
        .transform((_, value) => (value ? Number(value) : 0)),
      discount: Yup.number()
        .integer('Desconto inválido')
        .min(0, 'Desconto inválido')
        .max(99.99, 'Desconto inválido')
        .required('O desconto é obrigatório')
        .transform((_, value) => (value ? Number(value) : 0)),
      minimumStock: Yup.number()
        .min(0, 'Estoque mínimo inválido')
        .required('O estoque mínimo é obrigatório')
        .transform((_, value) => (value ? Number(value) : 0)),
      currentStock: Yup.number()
        .min(0, 'Estoque atual inválido')
        .required('O estoque atual é obrigatório')
        .transform((_, value) => (value ? Number(value) : 0)),
      addStock: Yup.number().transform((_, value) =>
        value ? Number(value) : 0,
      ),
    }),
  ),
});

type GridsProps = {
  formRef: React.RefObject<FormHandles>;
};

const Grids: React.FC<GridsProps> = (props) => {
  const { formRef } = props;

  const generateGridsModalRef = useRef<ModalHandles>(null);
  const editImagesModalRef = useRef<ModalHandles>(null);
  const modalRef = useRef<ModalHandles>(null);
  const {
    id,
    grids,
    sizesOptions,
    colorsOptions,
    handleAddGrids,
    handleEditGrid,
    handleChangeGridImage,
    handleToggleCanChangeCurrentStock,
    handleDeleteGrid: handleSoftDeleteGrid,
    handleChangeFeaturedGridProduct,
  } = useGridProduct();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [gridsToGenerate, setGridsToGenerate] = useState<{
    [colorId: number]: number[];
  }>({});
  const [selectedColor, setSelectedColor] = useState<number>();
  const [gridIdToEditImages, setGridIdToEditImages] = useState<
    number | string
  >();
  const [gridIdToDelete, setGridIdToDelete] = useState<number | string>();

  const [codeIsValid, setCodeIsValid] = useState(grids.map(() => false));
  const [codeIsLoading, setCodeIsLoading] = useState(grids.map(() => false));

  const handleChangeGridStatus = useCallback(
    (grid: Grid, status: boolean) => {
      handleEditGrid({
        ...grid,
        active: status,
        emphasis: status ? grid.emphasis : false,
      });
    },
    [handleEditGrid],
  );

  const handleTryDeleteGrid = useCallback((gridId: number | string) => {
    modalRef.current?.open();

    setGridIdToDelete(gridId);
  }, []);

  const handleDeleteGrid = useCallback(async () => {
    if (gridIdToDelete) {
      handleSoftDeleteGrid(gridIdToDelete);

      modalRef.current?.close();
    }
  }, [gridIdToDelete, handleSoftDeleteGrid]);

  const handleAttemptToEditImages = useCallback((gridId: number | string) => {
    setGridIdToEditImages(gridId);
    editImagesModalRef.current?.open();
  }, []);

  const handleCancelImagesEditing = useCallback(() => {
    editImagesModalRef.current?.close();

    setTimeout(() => {
      setGridIdToEditImages(undefined);
    }, 300);
  }, []);

  const handleCancelGridGeneration = useCallback(() => {
    generateGridsModalRef.current?.close();
    setGridsToGenerate({});
    setSelectedColor(undefined);
  }, []);

  const handleSelectColor = useCallback(
    (colorId: number) => {
      const colorExists = Object.keys(gridsToGenerate).includes(
        String(colorId),
      );

      if (colorExists) {
        setGridsToGenerate((oldState) => {
          const newState = oldState;

          delete newState[colorId];

          return newState;
        });
        setSelectedColor(undefined);
      } else {
        setGridsToGenerate((oldState) => ({
          ...oldState,
          [colorId]: [],
        }));
      }
    },
    [gridsToGenerate, sizesOptions],
  );

  const handleSelectSize = useCallback((sizeId: number, colorId: number) => {
    setGridsToGenerate((oldState) => ({
      ...oldState,
      [colorId]: (oldState[colorId] || []).includes(sizeId)
        ? oldState[colorId].filter((size) => size !== sizeId)
        : [...(oldState[colorId] || []), sizeId],
    }));
  }, []);

  const handleTryGenerateGrids = useCallback(() => {
    generateGridsModalRef.current?.open();

    const defaultGridsToGenerate: {
      [colorId: number]: number[];
    } = {};

    grids.forEach((grid) => {
      if (grid.colorId && grid.sizeId) {
        Object.assign(defaultGridsToGenerate, {
          [grid.colorId]: [
            ...(defaultGridsToGenerate[grid.colorId] || []),
            grid.sizeId,
          ],
        });
      }
    });

    setGridsToGenerate(defaultGridsToGenerate);
    setSelectedColor(undefined);
  }, [grids]);

  const handleGenerateGrids = useCallback(() => {
    const gridsToBeAdded: {
      [colorId: number]: number[];
    } = {};

    Object.keys(gridsToGenerate).forEach((colorId) => {
      const nonExistentGrids: number[] = [];

      gridsToGenerate[Number(colorId)].forEach((sizeId) => {
        const gridExists = !!grids.some(
          (grid) =>
            typeof grid.id === 'number' &&
            grid.colorId === Number(colorId) &&
            grid.sizeId === sizeId,
        );

        if (!gridExists) {
          nonExistentGrids.push(sizeId);
        }
      });

      Object.assign(gridsToBeAdded, {
        [colorId]: nonExistentGrids,
      });
    });

    handleAddGrids(gridsToBeAdded);
    generateGridsModalRef.current?.close();
    setGridsToGenerate({});
    setSelectedColor(undefined);
  }, [grids, gridsToGenerate, handleAddGrids]);

  const handleGridImage = useCallback((image?: File | string): string => {
    if (!image) {
      return PlaceholderGridImage;
    }

    if (image instanceof File) {
      return URL.createObjectURL(image);
    }

    return `${process.env.REACT_APP_SERVER_URL}/files/${image}`;
  }, []);

  const gridToUpdate = grids.find((grid) => grid.id === gridIdToEditImages);

  const req = async (code: string, gridIndex: number): Promise<void> => {
    const oldErrors = formRef.current?.getErrors();
    if (oldErrors) {
      delete oldErrors[`grids[${gridIndex}].code`];
    }
    formRef.current?.setErrors(oldErrors || {});

    if (code) {
      setCodeIsValid((oldState) => oldState.map(() => false));
      setCodeIsLoading((oldState) =>
        oldState.map((_, index) => index === gridIndex),
      );

      try {
        const data = {
          code,
        };

        if (typeof grids[gridIndex].id === 'number') {
          Object.assign(data, {
            productGridId: grids[gridIndex].id,
          });
        }

        await ProductsService.validatePDVCode(user.empresa_id, data);

        setCodeIsValid((oldState) =>
          oldState.map((_, index) => index === gridIndex),
        );
        setCodeIsLoading((oldState) =>
          oldState.map((value, index) => (index === gridIndex ? false : value)),
        );
      } catch (err) {
        console.log(err);
        formRef.current?.setErrors({
          [`grids[${gridIndex}].code`]: 'Código já em uso',
        });
        addToast({
          title: (err as AxiosError).message,
          type: 'error',
        });
        setCodeIsLoading((oldState) =>
          oldState.map((value, index) => (index === gridIndex ? false : value)),
        );
      }
    }
  };

  const reqDebouce: (code: string, gridIndex: number) => void = newDebounce(
    async (code: string, gridIndex: number): Promise<void> =>
      req(code, gridIndex),
    2000,
  );

  useEffect(() => {
    if (grids.length > 0) {
      setCodeIsValid(grids.map(() => false));
      setCodeIsLoading(grids.map(() => false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grids.length]);

  return (
    <Container>
      <Header>
        <h2>Grades</h2>
        <p>
          Aqui você pode definir em quais grades este produto estará disponível
        </p>
      </Header>
      <Button onClick={handleTryGenerateGrids} type="button" full>
        <FaPlus style={{ marginRight: 10 }} />
        Gerar grades
      </Button>
      <GridsList>
        {grids
          .sort((a, b) => a.order - b.order)
          .map((grid, gridIndex) => (
            <GridItem key={grid.id}>
              <GridItemHeader>
                <GridImage
                  placeholder={PlaceholderGridImage}
                  onClick={() => handleAttemptToEditImages(grid.id)}
                  url={handleGridImage(grid.images[0]?.file)}
                >
                  <div />
                </GridImage>
                <Input
                  name={`grids[${gridIndex}].name`}
                  placeholder="Nome da grade"
                />
                <Switch
                  checked={grid.active}
                  onChange={(checked) => handleChangeGridStatus(grid, checked)}
                  height={24}
                  width={48}
                  handleDiameter={15}
                />
              </GridItemHeader>
              <GridItemBody>
                <div>
                  <Select
                    id={`gridProductSize-${gridIndex}`}
                    name={`grids[${gridIndex}].size`}
                    label="Tamanho"
                    placeholder="Selecione uma opção"
                    maxMenuHeight={140}
                    isDisabled
                    options={sizesOptions}
                  />
                  <Select
                    id={`gridProductColor-${gridIndex}`}
                    name={`grids[${gridIndex}].color`}
                    label="Cor"
                    isDisabled
                    placeholder="Selecione uma opção"
                    maxMenuHeight={140}
                    options={colorsOptions}
                  />
                </div>
                <div>
                  <Input
                    name={`grids[${gridIndex}].price`}
                    label="Preço"
                    type="number"
                    step="0.01"
                    placeholder="Ex.: 632"
                  />
                  <CustomInput
                    isLoading={codeIsLoading[gridIndex]}
                    codeIsValid={codeIsValid[gridIndex]}
                    name={`grids[${gridIndex}].code`}
                    label="Código PDV"
                    placeholder="Ex.: 632"
                    onChange={(e) => {
                      reqDebouce(e.target.value, gridIndex);
                    }}
                  />
                  <Input
                    name={`grids[${gridIndex}].discount`}
                    label="Desconto"
                    type="number"
                    step="0.01"
                    placeholder="Ex.: 50"
                    suffix="%"
                  />
                </div>
                <div>
                  <Input
                    name={`grids[${gridIndex}].minimumStock`}
                    type="number"
                    step="0.01"
                    placeholder="Ex.: 5"
                    label="Estoque mínimo"
                  />
                  <div style={{ display: 'flex', flex: 1, gap: 10 }}>
                    <Input
                      name={`grids[${gridIndex}].currentStock`}
                      type="number"
                      step="0.01"
                      placeholder="Ex.: 80"
                      label="Estoque atual"
                      disabled={typeof grid.id === 'number'}
                    />
                    {typeof grid.id === 'number' && (
                      <HelpButton
                        type="button"
                        style={{ marginTop: 26, fontSize: 14 }}
                        onClick={() =>
                          handleToggleCanChangeCurrentStock(grid.id)
                        }
                      >
                        Alterar
                      </HelpButton>
                    )}
                  </div>
                  {grid.canChangeCurrentStock && (
                    <Input
                      name={`grids[${gridIndex}].addStock`}
                      type="number"
                      step="0.01"
                      placeholder="Ex.: 80"
                      label="Alterar estoque"
                      autoFocus
                    />
                  )}
                </div>
                <div>
                  <DeleteButton
                    type="button"
                    onClick={() => handleTryDeleteGrid(grid.id)}
                  >
                    <BiTrash size={24} />
                  </DeleteButton>
                  <CustomCheckbox
                    checked={grid.emphasis}
                    onClick={() => {
                      if (!grid.emphasis && !grid.active) {
                        addToast({
                          type: 'info',
                          title: 'Ative esta grade para destacá-la',
                        });
                        return;
                      }
                      handleChangeFeaturedGridProduct(grid);
                    }}
                  />
                </div>
              </GridItemBody>
            </GridItem>
          ))}
      </GridsList>

      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Você vai excluir esta grade.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={() => handleDeleteGrid()}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>

      <Modal ref={editImagesModalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <h1>Atualizar imagens</h1>
          <CloseModalButton onClick={handleCancelImagesEditing}>
            <BiX />
          </CloseModalButton>
        </ModalHeader>
        <ModalBody>
          {gridToUpdate && (
            <>
              <FileInput
                id="updateFirstGridProductImage"
                icon={BiImage}
                title="Escolha uma imagem"
                selectedImage={gridToUpdate.images[0]?.file}
                acceptableTypes=".jpeg,.jpg,.png"
                subtitle="Formatos: .jpeg, .jpg, .png"
                containerStyle={{
                  width: 238.6,
                  height: 158,
                }}
                onRemove={() =>
                  handleChangeGridImage(gridToUpdate.id, 0, undefined)
                }
                onSelectFile={(file) =>
                  handleChangeGridImage(gridToUpdate.id, 0, file)
                }
              />
              <FileInput
                id="updateSecondGridProductImage"
                icon={BiImage}
                title="Escolha uma imagem"
                selectedImage={gridToUpdate.images[1]?.file}
                acceptableTypes=".jpeg,.jpg,.png"
                subtitle="Formatos: .jpeg, .jpg, .png"
                containerStyle={{
                  width: 238.6,
                  height: 158,
                }}
                onRemove={() =>
                  handleChangeGridImage(gridToUpdate.id, 1, undefined)
                }
                onSelectFile={(file) =>
                  handleChangeGridImage(gridToUpdate.id, 1, file)
                }
              />
              <FileInput
                id="updateThirdGridProductImage"
                icon={BiImage}
                title="Escolha uma imagem"
                selectedImage={gridToUpdate.images[2]?.file}
                acceptableTypes=".jpeg,.jpg,.png"
                subtitle="Formatos: .jpeg, .jpg, .png"
                containerStyle={{
                  width: 238.6,
                  height: 158,
                }}
                onRemove={() =>
                  handleChangeGridImage(gridToUpdate.id, 2, undefined)
                }
                onSelectFile={(file) =>
                  handleChangeGridImage(gridToUpdate.id, 2, file)
                }
              />
            </>
          )}
        </ModalBody>
        <Button
          style={{ width: '80%', maxWidth: '300px' }}
          onClick={handleCancelImagesEditing}
        >
          Concluir
        </Button>
      </Modal>

      <Modal ref={generateGridsModalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <h1>Gerar grades</h1>
          <CloseModalButton onClick={handleCancelGridGeneration}>
            <BiX />
          </CloseModalButton>
        </ModalHeader>
        <ModalBody>
          <GenerateGridsTable>
            <GenerateGridsTableHeader>
              <div>
                <h2>Cores</h2>
              </div>
              <div>
                <h2>Tamanhos</h2>
              </div>
            </GenerateGridsTableHeader>
            <GenerateGridsTableBody>
              <div>
                {colorsOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setSelectedColor(color.value)}
                    className={
                      selectedColor === color.value ? 'active' : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      checked={Object.keys(gridsToGenerate).includes(
                        String(color.value),
                      )}
                      onChange={() => handleSelectColor(color.value)}
                      disabled={
                        !!grids.some(
                          (grid) =>
                            typeof grid.id === 'number' &&
                            grid.colorId === color.value,
                        )
                      }
                    />
                    <p>{color.label}</p>
                  </button>
                ))}
              </div>
              <div>
                {sizesOptions.map((size) => (
                  <button
                    key={size.value}
                    type="button"
                    disabled={!selectedColor}
                  >
                    <input
                      type="checkbox"
                      disabled={
                        !selectedColor ||
                        !!grids.some(
                          (grid) =>
                            typeof grid.id === 'number' &&
                            grid.sizeId === size.value &&
                            grid.colorId === selectedColor,
                        )
                      }
                      checked={gridsToGenerate[selectedColor || 0]?.includes(
                        size.value,
                      )}
                      onChange={() =>
                        handleSelectSize(size.value, selectedColor || 0)
                      }
                    />
                    <p>{size.label}</p>
                  </button>
                ))}
              </div>
            </GenerateGridsTableBody>
          </GenerateGridsTable>
          <ContainerButton>
            <EditColorsButton
              to="/configuracoes/7/0"
              style={{ padding: '4px 8px' }}
            >
              Editar cores
            </EditColorsButton>
            <EditSizesButton
              to="/configuracoes/7/1"
              style={{ padding: '4px 8px' }}
            >
              Editar tamanhos
            </EditSizesButton>
          </ContainerButton>
        </ModalBody>
        <Button full onClick={handleGenerateGrids}>
          Gerar grades
        </Button>
        <Button full variant="outlined" onClick={handleCancelGridGeneration}>
          Cancelar
        </Button>
      </Modal>
    </Container>
  );
};

export default Grids;
